import {observer} from 'mobx-react-lite';

// import components
import Badge from '@sdb/ui/components/atoms/Badge/index';
import Icon from '@sdb/ui/components/atoms/Icon/index';
import ListItemButton from '@sdb/ui/components/atoms/ListItemButton/index';
import ListItemIcon from '@sdb/ui/components/atoms/ListItemIcon/index';
import Tooltip from '@sdb/ui/components/atoms/Tooltip/index';
import Typography from '@sdb/ui/components/atoms/Typography/index';

// import stores
import announcekitStore from '@sdb/ui/client-store/announcekit.store';

// import hooks
import {useTranslation} from '@sdb/ui/translate/hooks/index';

// import types
import type {FC} from 'react';
import type {MenuActionButtonPropTypes} from '@sdb/ui/types/components/molecules/MenuActionButton.types';
import type {SxProp} from '@sdb/ui/types/theme/sx.types';

// Looks like a navigation rail item (type="navigationRail"), but icon and label use the tenant's
// primary colour (Beheer > Instellingen > kleur) so the button stands out.
// ListItemButton grows by default; without flexGrow 0 it shares the rail height with the menu list
// and the icon ends up halfway down instead of in the bottom-left corner.
const buttonSx: SxProp = {
    flexGrow: 0,
    flexShrink: 0,
    mb: 0,
    color: 'primary.main',
    '& .MuiListItemIcon-root': {color: 'primary.main'},
};

const labelSx: SxProp = {fontWeight: 600};

// Fills the NavigationRail actionButton slot, which sits below the menu list: bottom left of the
// screen. The rail only renders the slot when slotProps.actionButton has children; those children
// are meant for the mobile menu, so this component ignores them and renders its own label.
export const WhatsNewRailButton: FC<MenuActionButtonPropTypes> = observer(() => {
    const {t} = useTranslation();
    // Same check as the release notes badge in the profile menu, shown as a dot without a count.
    const hasNew = announcekitStore.unreads > 0;

    return (
        <Tooltip
            title={t('whatsNewTooltip')}
            placement="right"
        >
            <ListItemButton
                type="navigationRail"
                data-testid="WhatsNewRailButton"
                data-tourid="WhatsNewRailButton"
                aria-label={t('whatsNewTooltip')}
                onClick={() => announcekitStore.open()}
                sx={buttonSx}
            >
                <ListItemIcon>
                    <Badge
                        variant="dot"
                        color="warning"
                        invisible={!hasNew}
                        data-testid="WhatsNewRailButtonDot"
                    >
                        <Icon type="gift" />
                    </Badge>
                </ListItemIcon>
                <Typography
                    variant="label"
                    size="medium"
                    component="span"
                    noWrap
                    sx={labelSx}
                >
                    {t('whatsNew')}
                </Typography>
            </ListItemButton>
        </Tooltip>
    );
});

export default WhatsNewRailButton;
